import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { WaitingRoomItem } from "./network/useWaitingRoomDashboard";

/**
 * While a clinician is actively pointing at (or keyboard-focused within) the
 * queue, we freeze the *rendered* list so realtime socket updates can't reorder
 * a row out from under their cursor mid-click. The underlying React Query cache
 * keeps updating normally — we only defer when the view reconciles to it.
 *
 * Two guarantees:
 *  - Freezing never changes queue ordering semantics; it only delays *when* the
 *    live list is shown.
 *  - Because stale queue rows can delay patient care, the freeze is hard-capped:
 *    after MAX_FREEZE_MS of continuous freezing we force a reconcile (and re-arm),
 *    so the view is never more than MAX_FREEZE_MS behind live — even if a
 *    clinician parks their mouse over the table.
 */

// ConfigCat flag gating the whole live-queue freeze + animation experience.
export const LIVE_QUEUE_FREEZE_FLAG = "emr-live-queue-freeze";

// Grace period after the pointer/focus leaves before we reconcile to live.
const UNFREEZE_IDLE_MS = 400;
// Hard cap: force a reconcile to live after this long continuously frozen.
export const MAX_FREEZE_MS = 10_000;

export interface FreezeHandlers {
  onPointerEnter: () => void;
  onPointerLeave: () => void;
  onPointerDownCapture: () => void;
  onFocusCapture: () => void;
  onBlurCapture: () => void;
}

const NOOP = () => {};
// Stable no-op handlers used when the feature is flagged off, so the list
// behaves exactly as it did before (no freezing, no pill).
const NOOP_HANDLERS: FreezeHandlers = {
  onPointerEnter: NOOP,
  onPointerLeave: NOOP,
  onPointerDownCapture: NOOP,
  onFocusCapture: NOOP,
  onBlurCapture: NOOP,
};

export interface FrozenQueue {
  /** The list to render — the frozen snapshot while frozen, else live. */
  patients: WaitingRoomItem[];
  isFrozen: boolean;
  /** Patients present in live but not in the frozen snapshot (will appear). */
  addedCount: number;
  /** Patients present in the frozen snapshot but not in live (will disappear). */
  removedCount: number;
  /** Reconcile the snapshot to the current live list without unfreezing. */
  flush: () => void;
  freezeHandlers: FreezeHandlers;
}

export function useFrozenQueue(
  livePatients: WaitingRoomItem[] | undefined,
  enabled = true,
): FrozenQueue {
  const live = useMemo(() => livePatients ?? [], [livePatients]);

  // The list rendered while frozen. `null` means "not frozen — render live".
  const [snapshot, setSnapshot] = useState<WaitingRoomItem[] | null>(null);

  // Always-current view of the live list for use inside timers/callbacks.
  const liveRef = useRef(live);
  liveRef.current = live;

  const idleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const maxTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const clearIdle = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = undefined;
  }, []);
  const clearMax = useCallback(() => {
    if (maxTimer.current) clearTimeout(maxTimer.current);
    maxTimer.current = undefined;
  }, []);

  // Re-snapshot to the current live list (used by the "N new" pill and by the
  // max-freeze safety cap). Stays frozen.
  const flush = useCallback(() => {
    setSnapshot([...liveRef.current]);
  }, []);

  const armMaxTimer = useCallback(() => {
    clearMax();
    maxTimer.current = setTimeout(() => {
      // Hard cap hit: reconcile to live, then re-arm so clicks stay protected
      // while never exceeding MAX_FREEZE_MS of staleness.
      flush();
      armMaxTimer();
    }, MAX_FREEZE_MS);
  }, [flush, clearMax]);

  const beginFreeze = useCallback(() => {
    clearIdle();
    setSnapshot((prev) => (prev ? prev : [...liveRef.current]));
    // Arm the cap only when starting a *fresh* freeze so the window counts from
    // the moment interaction began, not from each re-entry.
    if (!maxTimer.current) armMaxTimer();
  }, [armMaxTimer, clearIdle]);

  const scheduleUnfreeze = useCallback(() => {
    clearIdle();
    idleTimer.current = setTimeout(() => {
      setSnapshot(null);
      clearMax();
    }, UNFREEZE_IDLE_MS);
  }, [clearIdle, clearMax]);

  useEffect(
    () => () => {
      clearIdle();
      clearMax();
    },
    [clearIdle, clearMax],
  );

  const { addedCount, removedCount } = useMemo(() => {
    if (!snapshot) return { addedCount: 0, removedCount: 0 };
    const shownIds = new Set(snapshot.map((p) => p.encounter.id));
    const liveIds = new Set(live.map((p) => p.encounter.id));
    return {
      addedCount: live.filter((p) => !shownIds.has(p.encounter.id)).length,
      removedCount: snapshot.filter((p) => !liveIds.has(p.encounter.id)).length,
    };
  }, [snapshot, live]);

  const freezeHandlers = useMemo<FreezeHandlers>(
    () => ({
      onPointerEnter: beginFreeze,
      onPointerLeave: scheduleUnfreeze,
      // Mousedown target guard: guarantees the list is frozen the instant a
      // press begins, so nothing can shift between pointerdown and click.
      onPointerDownCapture: beginFreeze,
      onFocusCapture: beginFreeze,
      onBlurCapture: scheduleUnfreeze,
    }),
    [beginFreeze, scheduleUnfreeze],
  );

  // Flagged off: behave exactly as before — render live, never freeze. (All
  // hooks above still run, so hook order is stable across flag changes.)
  if (!enabled) {
    return {
      patients: live,
      isFrozen: false,
      addedCount: 0,
      removedCount: 0,
      flush: NOOP,
      freezeHandlers: NOOP_HANDLERS,
    };
  }

  return {
    patients: snapshot ?? live,
    isFrozen: snapshot !== null,
    addedCount,
    removedCount,
    flush,
    freezeHandlers,
  };
}
